import type { Group, GroupsConfig, Id, Match, Phase, StandingRow, Tournament } from '../types.js';
import { effectiveRules } from '../rules/presets.js';
import { computeStandings } from '../standings/standings.js';
import { sideOf, slotTeamIds } from '../slots.js';

/** Tabellen je Gruppe einer Gruppenphase (berechnet, nie gespeichert) */
export function phaseStandings(t: Tournament, phaseId: Id): { group: Group; rows: StandingRow[] }[] {
  for (const comp of t.competitions) {
    const phase = comp.phases.find((p) => p.id === phaseId);
    if (!phase) continue;
    if (phase.type !== 'groups') return [];
    const cfg = phase.config as GroupsConfig;
    const rules = effectiveRules(comp.ruleSet, phase.ruleSetOverride);
    return phase.groups.map((group) => {
      const opts: Parameters<typeof computeStandings>[3] = { qualifiers: cfg.qualifiersPerGroup };
      if (group.lotOrder) opts.lotOrder = group.lotOrder;
      return {
        group,
        rows: computeStandings(
          group.teamIds,
          phase.matches.filter((m) => m.groupId === group.id),
          rules,
          opts,
        ),
      };
    });
  }
  return [];
}

export interface FinalRank {
  rank: number;
  teamId: Id;
}

/** Eine Zeile der Gesamttabelle über alle Gruppen */
export interface OverallRow {
  rank: number;
  teamId: Id;
  /** Gruppe, aus der die Zeile stammt — für die Anzeige */
  groupName: string;
  /** Platz innerhalb der eigenen Gruppe */
  groupRank: number;
  played: number;
  wins: number;
  losses: number;
  /** Punkte nach Haralds Lesart: gewonnene minus verlorene Spiele */
  points: number;
  /** Differenz der Würfe über alle Spiele */
  scoreDiff: number;
  scoreFor: number;
  scoreAgainst: number;
}

/**
 * Gesamttabelle über alle Gruppen (Harald, 09/2026):
 *
 * > „Die Tabelle über alle. Reihenfolge: Punkte (gewonnene − verlorene Spiele),
 * > dann gewonnene Spiele, verlorene Spiele und danach die Punktdifferenz der
 * > Einzelergebnisse aus in allen Spielen erzielter Würfe."
 *
 * Bewusst **nicht** `rankAcrossGroups`: Das rechnet mit den Punkten aus dem
 * Regelwerk (z. B. 2 je Sieg) und normiert bei ungleich großen Gruppen pro
 * Spiel. Hier zählt genau die Kette oben, und „Punkte" sind Siege − Niederlagen
 * — bei einem Turnier mit 2 Punkten je Sieg also eine andere Zahl als in der
 * Gruppentabelle. Gleichstand über alle vier Stufen hinweg heißt gleicher Rang;
 * die nächste Zeile springt entsprechend weiter (1, 2, 2, 4).
 */
export function overallStandings(t: Tournament, phaseId: Id): OverallRow[] {
  const zeilen: OverallRow[] = [];
  for (const { group, rows } of phaseStandings(t, phaseId)) {
    for (const r of rows) {
      zeilen.push({
        rank: 0,
        teamId: r.teamId,
        groupName: group.name,
        groupRank: r.rank,
        played: r.played,
        wins: r.wins,
        losses: r.losses,
        points: r.wins - r.losses,
        scoreDiff: r.scoreDiff,
        scoreFor: r.scoreFor,
        scoreAgainst: r.scoreAgainst,
      });
    }
  }
  const schluessel = (r: OverallRow) => [r.points, r.wins, -r.losses, r.scoreDiff];
  zeilen.sort((a, b) => {
    const x = schluessel(a);
    const y = schluessel(b);
    for (let i = 0; i < x.length; i++) {
      const d = (y[i] as number) - (x[i] as number);
      if (d !== 0) return d;
    }
    return 0;
  });
  zeilen.forEach((r, i) => {
    const vor = zeilen[i - 1];
    r.rank = vor && schluessel(vor).join() === schluessel(r).join() ? vor.rank : i + 1;
  });
  return zeilen;
}

/**
 * Endplatzierung eines KO-Baums: Finalsieger 1, Finalverlierer 2, Spiel um
 * Platz 3 (falls vorhanden) 3/4, danach Verlierer je Runde geteilt.
 */
export function bracketRanking(phase: Phase): FinalRank[] {
  if (!phase.bracket) return [];
  const out: FinalRank[] = [];
  const byId = new Map(phase.matches.map((m) => [m.id, m]));
  const finalId = phase.bracket.roundMatchIds[phase.bracket.rounds - 1]?.[0];
  const final = finalId ? byId.get(finalId) : undefined;
  const winnerOf = (m: Match | undefined): Id | undefined => {
    if (!m?.result?.winner) return undefined;
    const s = m.result.winner === 'A' ? m.a : m.b;
    return s.kind === 'team' ? s.teamId : undefined;
  };
  const loserOf = (m: Match | undefined): Id | undefined => {
    if (!m?.result?.winner) return undefined;
    const s = m.result.winner === 'A' ? m.b : m.a;
    return s.kind === 'team' ? s.teamId : undefined;
  };
  const w = winnerOf(final);
  const l = loserOf(final);
  if (w) out.push({ rank: 1, teamId: w });
  if (l) out.push({ rank: 2, teamId: l });
  let next = 3;
  const third = phase.bracket.thirdPlaceMatchId ? byId.get(phase.bracket.thirdPlaceMatchId) : undefined;
  const thirdPlayed = !!third?.result?.winner;
  if (thirdPlayed) {
    const tw = winnerOf(third);
    const tl = loserOf(third);
    if (tw) out.push({ rank: 3, teamId: tw });
    if (tl) out.push({ rank: 4, teamId: tl });
    next = 5;
  }
  // Ohne gespieltes Spiel um Platz 3 (abgesagt oder abgewählt) teilen sich die Halbfinal-Verlierer Platz 3
  for (let r = phase.bracket.rounds - (thirdPlayed ? 2 : 1); r >= 1; r--) {
    const ids = phase.bracket.roundMatchIds[r - 1] ?? [];
    const losers = ids.map((id) => loserOf(byId.get(id))).filter((x): x is Id => !!x);
    for (const teamId of losers) out.push({ rank: next, teamId });
    next += losers.length;
  }
  return out;
}

/**
 * Endplatzierung einer Phase mit von Hand gestarteten KO-Runden
 * (docs/notes/ko-runden.md): Sieger der letzten Runde zuerst, danach die in der
 * jeweils letzten Runde Ausgeschiedenen — Runde für Runde rückwärts. Wer in
 * einer späteren Runde wieder mitgespielt hat (Aufstocken), zählt zu dieser
 * späteren Runde. Ausgeschiedene derselben Runde teilen sich einen Rang, wie in
 * `bracketRanking`.
 */
export function koRanking(phase: Phase): FinalRank[] {
  const rounds = phase.koRounds ?? [];
  if (rounds.length === 0) return [];
  const byId = new Map(phase.matches.map((m) => [m.id, m]));
  // Letzte Runde je Team — nur dort scheidet es aus
  const lastRound = new Map<Id, number>();
  rounds.forEach((r, i) => {
    for (const id of r.entrants) lastRound.set(id, i);
  });
  const out: FinalRank[] = [];
  let next = 1;
  for (let i = rounds.length - 1; i >= 0; i--) {
    const r = rounds[i] as (typeof rounds)[number];
    const won = new Set<Id>();
    for (const id of r.matchIds) {
      const m = byId.get(id);
      if (!m?.result?.winner) continue;
      const s = m.result.winner === 'A' ? m.a : m.b;
      if (s.kind === 'team') won.add(s.teamId);
    }
    const here = r.entrants.filter((id) => lastRound.get(id) === i);
    for (const group of [here.filter((id) => won.has(id)), here.filter((id) => !won.has(id))]) {
      for (const teamId of group) out.push({ rank: next, teamId });
      next += group.length;
    }
  }
  return out;
}

/** Endplatzierung je Phasentyp — KO-Baum oder von Hand gestartete KO-Runden */
export function phaseRanking(phase: Phase): FinalRank[] {
  if (phase.type === 'ko_rounds') return koRanking(phase);
  return bracketRanking(phase);
}

/** Alle Spiele eines Teams, chronologisch (für Team-Link / Player App) */
export function teamSchedule(t: Tournament, teamId: Id): Match[] {
  const out: Match[] = [];
  for (const comp of t.competitions) {
    for (const phase of comp.phases) {
      for (const m of phase.matches) {
        if (sideOf(m, teamId)) out.push(m);
      }
    }
  }
  return out.sort((x, y) => (x.endedAt ?? Infinity) - (y.endedAt ?? Infinity) || x.round - y.round || x.number - y.number);
}

/** Invarianten aus docs/03 — für Tests und Debug-Anzeige */
export function checkInvariants(t: Tournament): string[] {
  const problems: string[] = [];
  const runningTeams = new Map<Id, Id>();
  const courtMatches = new Map<Id, Id[]>();
  for (const comp of t.competitions) {
    for (const phase of comp.phases) {
      for (const m of phase.matches) {
        if (m.status === 'running') {
          for (const s of [m.a, m.b]) {
            if (s.kind !== 'team') {
              problems.push(`Spiel ${m.id} läuft ohne feste Teams`);
              continue;
            }
            for (const id of slotTeamIds(s)) {
              const other = runningTeams.get(id);
              if (other) problems.push(`Team ${id} spielt gleichzeitig in ${other} und ${m.id}`);
              runningTeams.set(id, m.id);
            }
          }
          if (!m.courtId) problems.push(`Spiel ${m.id} läuft ohne Feld`);
          else courtMatches.set(m.courtId, [...(courtMatches.get(m.courtId) ?? []), m.id]);
        }
        if ((m.status === 'finished' || m.status === 'walkover') && !m.result) problems.push(`Spiel ${m.id} beendet ohne Ergebnis`);
      }
      if (phase.type === 'groups') {
        const seen = new Set<Id>();
        for (const g of phase.groups) {
          for (const id of g.teamIds) {
            if (seen.has(id)) problems.push(`Team ${id} in zwei Gruppen`);
            seen.add(id);
          }
        }
      }
    }
  }
  for (const [courtId, ids] of courtMatches) if (ids.length > 1) problems.push(`Feld ${courtId} hat ${ids.length} laufende Spiele`);
  for (const c of t.courts) {
    if (c.currentMatchId && !courtMatches.get(c.id)?.includes(c.currentMatchId)) problems.push(`Feld ${c.id} zeigt auf ${c.currentMatchId}, das nicht läuft`);
  }
  return problems;
}
